import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import FilterSelect from "../../components/forms/FilterSelect";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  CARD,
  EmptyState,
  ErrorState,
  Panel,
  Refreshing,
  STALE,
} from "../../components/timetable/PanelKit";
import SegmentedControl from "../../components/timetable/SegmentedControl";
import Skeleton from "../../components/Skeleton/Skeleton";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { exportTimetableGrid, getTimetableGrid } from "../../services/timetableService";
import { getGroups } from "../../services/groupService";
import { subjectColor } from "../../components/timetable/BoardCard";
import { slotKey } from "../../components/timetable/useBoard";
import {
  WEEKDAY_KEY,
  downloadBlob,
  rowsOf,
  shortName,
} from "../../utils/helpers/timetableDisplay";
import { naturalCompare } from "../../utils/helpers/locale";
import { runErrorMessage } from "../../components/timetable/runErrors";
import { getRooms } from "../../services/schedulingService";

// ---------------------------------------------------------------------------
// The school's LIVE timetable — the one that is actually in force, not a draft.
//
// This is the surface the product was missing entirely. The only way to read a
// committed timetable used to be one teacher at a time (`?teacher=` was a
// REQUIRED filter), so there was no class week to hand a form tutor, no room
// week, and no whole-school view to answer "who is free Tuesday P4?" — and
// nothing at all to print.
//
// Getting a timetable OUT is the Excel export, not browser print. Print was
// tried and removed: the app shell is `h-screen overflow-hidden` around an
// `overflow-y-auto` <main>, so a print is one page (browsers paginate the
// document, not the inside of a scroll box); browsers also do not paginate
// horizontally, so the 41-column summary lost everything past Tuesday; and
// unwinding the shell far enough to fix both made the rest of the page overlap
// itself. Fighting an app shell from a stylesheet is the wrong shape for this.
// If PDF is wanted later, render a STANDALONE document — a fresh window with
// only the grid in it — so there is no shell to fight. Excel prints fine from
// Excel in the meantime.
// ---------------------------------------------------------------------------

const AXES = ["school", "class", "teacher", "room"];
// "compact" = subject code only; "full" adds a short teacher name per cell.
const DENSITIES = ["compact", "full"];

// The query params for an axis+entity, in ONE place. `load` and `onExport` each
// carried their own copy of this ternary plus the same empty-target fallback —
// if the two drifted, the exported workbook would silently scope differently
// from the grid on screen, which is the one mismatch nobody would ever catch
// because the file opens in another application.
const axisParams = (axis, entity) => {
  if (axis === "school" || !entity) return {};
  if (axis === "class") return { group: entity };
  if (axis === "teacher") return { teacher: entity };
  if (axis === "room") return { room: entity };
  return {};
};

// The leading grade of a class code ("10A" -> "10"), used to draw a heavier rule
// where one grade ends and the next begins.
const gradeOf = (code) => (String(code || "").match(/^\d+/) || [""])[0];

export default function GridPage() {
  const { t } = useTranslation();

  const [axis, setAxis] = useState("school");
  const [entity, setEntity] = useState("");
  const [grid, setGrid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // "compact" = subject code only; "full" also bakes a short teacher name into
  // every cell. Only meaningful on the summary — the week layouts have room for
  // everything already.
  const [density, setDensity] = useState("compact");

  // Pickers for the non-school axes.
  const [groups, setGroups] = useState([]);
  const [rooms, setRooms] = useState([]);
  // The teacher picker's source: the whole school's entries, captured from the
  // first UNSCOPED grid response and then left alone.
  //
  // Deriving it from `grid.entries` directly collapses — selecting a teacher
  // refetches the grid scoped to that teacher, the entries then contain only
  // their rows, and the list the selection came from shrinks to the one person
  // already chosen, so a second teacher can never be picked. Kept as "teachers
  // who actually have lessons", which is more useful than every account with a
  // teacher role.
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    getGroups({ params: { page_size: MAX_PAGE_SIZE }, signal: controller.signal })
      .then((data) => setGroups(rowsOf(data)))
      .catch(() => setGroups([]));
    getRooms({ params: { page_size: MAX_PAGE_SIZE }, signal: controller.signal })
      .then((data) => setRooms(rowsOf(data)))
      .catch(() => setRooms([]));
    return () => controller.abort();
  }, []);

  // Fetches the grid for the CURRENT axis. The picker sources are separate —
  // see `roster` above for why they have to be.
  const load = useCallback(
    (signal) => {
      setIsLoading(true);
      setLoadError(null);
      // An axis that needs a target but has none yet falls back to the whole
      // school, rather than an empty screen with no explanation.
      const params = axisParams(axis, entity);
      const unscoped = Object.keys(params).length === 0;
      return getTimetableGrid({ params, signal })
        .then((data) => {
          setGrid(data);
          // Seed the picker source from the response we already have rather
          // than firing a second, identical request for it. `axis` starts at
          // "school", so on a normal visit this lands on the first load and
          // the extra round trip never happens at all.
          if (unscoped) setRoster(data?.entries || []);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          setLoadError(err);
          setGrid(null);
        })
        .finally(() => {
          if (!signal?.aborted) setIsLoading(false);
        });
    },
    [axis, entity],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Excel rather than only print, because a whole-school summary is 41 columns
  // on a five-day eight-period week and browsers do not paginate horizontally:
  // everything past the page width is clipped, so a printed summary silently
  // loses Wednesday onward. A spreadsheet has no page width.
  const onExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await exportTimetableGrid({
        params: axisParams(axis, entity),
      });
      downloadBlob(blob, filename);
    } catch (err) {
      // Through the shared formatter like every other timetable failure, so a
      // coded 400 renders localized rather than as one generic string.
      toast.error(runErrorMessage(err, t, t("timetable.grid.exportFailed")));
    } finally {
      setIsExporting(false);
    }
  }, [axis, entity, t]);

  const entries = useMemo(() => grid?.entries || [], [grid]);

  const teachers = useMemo(() => {
    const seen = new Map();
    roster.forEach((row) => {
      if (row.teacher) seen.set(row.teacher.uuid, row.teacher.name);
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => naturalCompare(a.label, b.label));
  }, [roster]);

  const options = useMemo(() => {
    if (axis === "class") {
      return groups
        .map((g) => ({ value: g.uuid, label: g.code || g.name }))
        .sort((a, b) => naturalCompare(a.label, b.label));
    }
    if (axis === "room") {
      return rooms
        .map((r) => ({ value: r.uuid, label: r.code ? `${r.code} — ${r.name}` : r.name }))
        .sort((a, b) => naturalCompare(a.label, b.label));
    }
    if (axis === "teacher") return teachers;
    return [];
  }, [axis, groups, rooms, teachers]);

  // Keep the picker on a real option whenever the axis changes.
  useEffect(() => {
    if (axis === "school") {
      setEntity("");
      return;
    }
    setEntity((current) =>
      options.some((o) => o.value === current) ? current : options[0]?.value || "",
    );
  }, [axis, options]);

  // slot -> rows. Co-taught lessons share a `lesson_key` and a multi-teacher
  // lesson is several rows; collapsing on that key is what stops one lesson
  // rendering as N rival cards stacked in the same cell.
  const bySlot = useMemo(() => {
    // Only the axis actually on screen needs its index. Building both meant the
    // landing (school) view allocated 40 Maps over 1200 lesson objects that
    // nothing reads.
    if (axis === "school") return null;
    const map = new Map();
    entries.forEach((row) => {
      const key = slotKey(row.weekday, row.period);
      const bucket = map.get(key) || new Map();
      const lessonKey = row.lesson_key || row.uuid;
      const existing = bucket.get(lessonKey);
      if (existing) {
        if (row.teacher && !existing.teachers.some((x) => x.uuid === row.teacher.uuid)) {
          existing.teachers.push(row.teacher);
        }
      } else {
        bucket.set(lessonKey, {
          key: lessonKey,
          group: row.group,
          subject: row.subject,
          room: row.room,
          teachers: row.teacher ? [row.teacher] : [],
        });
      }
      map.set(key, bucket);
    });
    return map;
  }, [entries, axis]);

  // The SUMMARY layout's index: class -> slot -> the one lesson that class has
  // there. A class can only be in one place at a time (the solver enforces it
  // structurally), so this is a single lesson per cell, not a bucket.
  const summary = useMemo(() => {
    if (axis !== "school") return [];
    const classes = new Map();
    entries.forEach((row) => {
      const uuid = row.group?.uuid;
      if (!uuid) return;
      let entry = classes.get(uuid);
      if (!entry) {
        entry = { group: row.group, slots: new Map() };
        classes.set(uuid, entry);
      }
      const key = slotKey(row.weekday, row.period);
      const existing = entry.slots.get(key);
      if (existing) {
        // Co-teaching: same lesson, another teacher row.
        if (row.teacher && !existing.teachers.some((x) => x.uuid === row.teacher.uuid)) {
          existing.teachers.push(row.teacher);
        }
        return;
      }
      entry.slots.set(key, {
        subject: row.subject,
        room: row.room,
        teachers: row.teacher ? [row.teacher] : [],
      });
    });
    return [...classes.values()].sort((a, b) =>
      naturalCompare(a.group.code || a.group.name, b.group.code || b.group.name),
    );
  }, [entries, axis]);

  const weekdays = grid?.weekdays || [];
  const periods = grid?.periods || [];
  const bells = grid?.period_times || {};
  const isEmpty = !isLoading && entries.length === 0;

  // Changing the PERSON on a teacher week is a refetch of the same shape: the
  // week that is on screen stays there, dimmed, while the next one arrives.
  // Changing the AXIS is not — a class week and the whole-school summary are
  // different tables, so holding the old one in place would be showing the
  // wrong document rather than a stale one. The response carries the axis it
  // was built for, so no extra state is needed to tell the two apart.
  const isStaleAxis = Boolean(grid) && grid?.scope?.axis !== axis;
  const isFirstLoad = isLoading && (!grid || isStaleAxis);
  const isRefreshing = isLoading && !isFirstLoad;

  // The PICKER's label wins over the response's scope, because during a refetch
  // they disagree: the response still describes the teacher you just navigated
  // away from, and the heading is the only thing on this page that says whose
  // week is on screen. Falls back to the response for the first paint, when
  // there is no picker (the school axis) or the option list has not landed.
  const heading = useMemo(() => {
    if (axis !== "school") {
      const picked = options.find((option) => option.value === entity);
      if (picked) return picked.label;
    }
    const s = grid?.scope || {};
    if (s.class) return s.class.code || s.class.name;
    if (s.teacher) return s.teacher.name;
    if (s.room) return s.room.code || s.room.name;
    return t("timetable.grid.wholeSchool");
  }, [axis, options, entity, grid, t]);

  return (
    <Panel>
      {/* `heading` used to exist only inside an sr-only caption, so on the
          class/teacher/room axes the only on-screen indication of WHOSE week
          this is was the value sitting in a <select>. It is no longer wrapped
          in a bare <div>: PanelHeader carried its own `mb-2`, so that wrapper
          made header→toolbar 28px here while every other gap on the panel was
          20px. */}
      <PanelHeader
        title={heading}
        /* Withheld mid-refetch: it counts the week being replaced, and beside a
           heading that already names the next one it would simply be wrong. */
        count={isRefreshing ? undefined : entries.length || undefined}
        subtitle={t("timetable.grid.subtitle")}
      />

      {/* -- axis + picker + export ------------------------------------------ */}
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          options={AXES}
          value={axis}
          onChange={setAxis}
          ariaLabel={t("timetable.grid.axisAria")}
          renderLabel={(option) => t(`timetable.gridAxis.${option}`)}
        />

        {axis !== "school" && (
          // Type-ahead past eight: the teacher axis lists everyone who has a
          // lesson (45 on a real school) and the class axis every class (30).
          // Disabled while the list is genuinely empty — the class and room
          // pickers are fed by their own requests, and a dropdown that opens
          // onto nothing reads as broken rather than as not-loaded-yet.
          <FilterSelect
            options={options}
            value={entity}
            onChange={setEntity}
            disabled={options.length === 0}
            ariaLabel={t(`timetable.gridAxis.${axis}`)}
            placeholder={
              options.length === 0
                ? t("common.loading")
                : t(`timetable.gridAxis.${axis}`)
            }
            minWidthClass="min-w-[14rem]"
            searchable={options.length > 8}
            searchPlaceholder={t("common.search")}
            emptyText={t("timetable.grid.noPickerMatches")}
          />
        )}

        {axis === "school" && (
          <p className="max-w-[16rem] text-[11px] font-medium leading-snug text-slate-500">
            {t("timetable.grid.hoverHint")}
          </p>
        )}

        {axis === "school" && (
          <SegmentedControl
            options={DENSITIES}
            value={density}
            onChange={setDensity}
            ariaLabel={t("timetable.grid.densityAria")}
            renderLabel={(option) => t(`timetable.grid.density.${option}`)}
          />
        )}

        {/* The whole-school export is not the same document as the screen: it
            expands into a summary plus one sheet per class and per teacher,
            which is what a school actually hands out. */}
        {/* Secondary, not a solid saturated fill. Export is the least-used
            action on the page and was rendered heavier than the axis control,
            which is the actual primary — and emerald means "success" everywhere
            else in this product, not "download". */}
        {isRefreshing && (
          <span className="ml-auto">
            <Refreshing />
          </span>
        )}

        <button
          type="button"
          onClick={onExport}
          disabled={isEmpty || isLoading || isExporting}
          title={t("timetable.grid.excelHint")}
          className={`${isRefreshing ? "" : "ml-auto"} ${BTN.secondary}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
          </svg>
          {isExporting ? t("timetable.grid.exporting") : t("timetable.grid.excel")}
        </button>
      </div>

      {isFirstLoad ? (
        <GridSkeleton axis={axis} />
      ) : loadError ? (
        /* Now offers a retry. It used to be a dead rose banner: a failed grid
           load left the manager with no way back other than a full reload. */
        <ErrorState title={t("timetable.grid.loadFailed")} onRetry={() => load()} />
      ) : isEmpty ? (
        <EmptyGrid />
      ) : axis === "school" ? (
        /* One row per CLASS, columns = weekday x period — aSc's "summary"
           layout. The week layout below cannot serve this axis: it stacks every
           lesson in a (weekday, period) cell, which for a ten-class school is
           ten cards per cell and for a real thirty-class one is unreadable on
           screen and unprintable on paper. */
        <div className={isRefreshing ? STALE : undefined} aria-busy={isRefreshing}>
          <SummaryGrid
            classes={summary}
            weekdays={weekdays}
            periods={periods}
            density={density}
          />
        </div>
      ) : (
        <div
          aria-busy={isRefreshing}
          className={`overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3 ${
            isRefreshing ? STALE : ""
          }`}
        >
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              {t("timetable.grid.tableCaption", { scope: heading })}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="w-16 px-2 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {t("timetable.grid.period")}
                </th>
                {weekdays.map((day) => (
                  <th
                    key={day}
                    scope="col"
                    className="px-2 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500"
                  >
                    {t(`timetable.weekday.${WEEKDAY_KEY[day]}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((period) => (
                <tr key={period} className="align-top">
                  {/* P-prefix and the bell times, matching the draft board's
                      gutter. A bare period index is not something a teacher
                      handed this sheet can act on — which is also why the
                      times are slate-500 and not the slate-300 all three
                      period gutters used to share: at 1.5:1 on white, the
                      clock this comment calls load-bearing was a smudge.
                      Change it here, in BoardGrid and in the rule slot picker
                      together, or two views of one week drift apart. */}
                  <th
                    scope="row"
                    className="whitespace-nowrap px-2 py-1.5 text-right align-top"
                  >
                    <span className="block text-[11px] font-black text-slate-500 tabular-nums">
                      P{period}
                    </span>
                    {bells[String(period)]?.start && (
                      <span className="block text-[10px] font-medium text-slate-500 tabular-nums">
                        {bells[String(period)].start}–{bells[String(period)].end}
                      </span>
                    )}
                  </th>
                  {weekdays.map((day) => {
                    const bucket = bySlot?.get(slotKey(day, period));
                    const lessons = bucket ? [...bucket.values()] : [];
                    return (
                      <td key={day} className="p-1 min-w-[9rem]">
                        {lessons.length === 0 ? (
                          <div className="h-full min-h-[3rem] rounded-lg border border-slate-100 bg-slate-50/50" />
                        ) : (
                          <div className="space-y-1">
                            {lessons.map((lesson) => (
                              <Cell key={lesson.key} lesson={lesson} axis={axis} />
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// The whole school on one sheet: classes down the side, the week across the top.
// Each cell holds at most one lesson, because a class cannot be in two places at
// once — which is exactly what makes this layout readable where stacking every
// class's lesson into a 5x8 grid is not.
//
// The problem this layout creates is that a cell is ~40px wide, so only the
// subject code fits — and a timetable without the teacher is half a timetable.
// Two answers, because they serve different moments:
//
//   `density` — "Subject + teacher" bakes a short teacher name onto a second
//     line in every cell. Right when you are scanning for a person, and worth
//     the extra width once the school is small enough or the screen big enough.
//   the hover/focus CARD — full detail for ONE cell without widening anything.
//     Right when you are reading the grid and want to know about this lesson.
//
// It is deliberately NOT the native `title` tooltip it replaced: that waits
// about a second, cannot be styled, never appears on touch, and is skipped by
// most screen readers. The card shows instantly, opens on FOCUS as well as
// hover so it is keyboard-reachable, and each cell is a real <button> so a tap
// works. The full detail is also on the button's `aria-label`, so assistive
// tech gets it whether or not the card is rendered.
function SummaryGrid({ classes, weekdays, periods, density }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(null);

  // Escape closes the card without moving focus — the same way out the board
  // gives for a pending placement. Scroll and resize close it too: the card is
  // `position: fixed` at the cell's VIEWPORT rect, so anything that moves the
  // cell without re-firing hover would leave the card stranded next to the
  // wrong lesson. `capture` because the table scrolls horizontally in its own
  // container, and that scroll never reaches window.
  useEffect(() => {
    if (!active) return undefined;
    const dismiss = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      setActive(null);
    };
    document.addEventListener("keydown", dismiss);
    document.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("keydown", dismiss);
      document.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [active]);

  // Opening is DELAYED. Without it, dragging the pointer across a dense row
  // strobes a 260px shadowed panel around the screen once per cell — on the
  // summary axis this card is the only way to read a cell, so its jitter is the
  // loudest motion on the page. 120ms is below the threshold where a deliberate
  // hover feels laggy and well above the time a passing pointer spends on one
  // 44px cell. Keyboard focus opens immediately: focus is always deliberate.
  const timerRef = useRef(null);
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  useEffect(() => clearTimer, []);

  const open = useCallback((event, payload, { immediate = false } = {}) => {
    const rect = event.currentTarget.getBoundingClientRect();
    clearTimer();
    if (immediate) {
      setActive({ ...payload, rect });
      return;
    }
    timerRef.current = setTimeout(() => setActive({ ...payload, rect }), 120);
  }, []);

  const close = useCallback(() => {
    clearTimer();
    setActive(null);
  }, []);

  // The 40 column descriptors, computed once instead of per row per cell.
  // `dayIndex % 2`, the day-boundary test and the two constant class strings
  // were each evaluated rows x days x periods times (1200 on a 30-class board)
  // to pick between two fixed values — and the aria-label's two `t()` calls
  // went with them, ~2400 i18next lookups on every hover.
  const columns = useMemo(
    () =>
      weekdays.flatMap((day, dayIndex) =>
        periods.map((period, i) => ({
          key: slotKey(day, period),
          day,
          period,
          dayName: t(`timetable.weekday.${WEEKDAY_KEY[day]}`),
          periodName: t("timetable.grid.periodN", { n: period }),
          band: dayIndex % 2 ? "bg-slate-50" : "",
          headBand: dayIndex % 2 ? "bg-slate-50" : "bg-white",
          edge: i === 0 && dayIndex > 0 ? "border-l-2 border-l-slate-300" : "",
        })),
      ),
    [weekdays, periods, t],
  );

  // Row index of each grade change, so a heavier rule can separate grade blocks.
  const gradeBreaks = useMemo(() => {
    const set = new Set();
    classes.forEach((row, i) => {
      if (i === 0) return;
      const prev = gradeOf(classes[i - 1].group.code || classes[i - 1].group.name);
      const here = gradeOf(row.group.code || row.group.name);
      if (prev !== here) set.add(i);
    });
    return set;
  }, [classes]);

  return (
    <div className="relative">
      {/* `border-separate` + `border-spacing-0`, NOT `border-collapse`: borders
          on a `position: sticky` cell are not painted under collapse, which is
          what left the frozen class column with no edge at all. */}
      <div className="relative overflow-auto rounded-2xl border border-slate-200 bg-white max-h-[70vh]">
        <table className="border-separate border-spacing-0 text-left">
          <caption className="sr-only">
            {t("timetable.grid.tableCaption", { scope: t("timetable.grid.wholeSchool") })}
          </caption>
          <thead>
            <tr>
              <th
                scope="col"
                rowSpan={2}
                className="sticky left-0 top-0 z-30 bg-white px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 border-b-2 border-r-2 border-slate-200"
              >
                {t("timetable.grid.class")}
              </th>
              {weekdays.map((day, dayIndex) => (
                <th
                  key={day}
                  scope="colgroup"
                  colSpan={periods.length}
                  /* Left-ALIGNED to the band's first column, not centred over a
                     352px span with nothing tying label to columns. The band
                     wash + a 2px rule are what let the eye chunk 40 columns
                     into five days. */
                  className={`sticky top-0 z-20 h-8 px-2 text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200 ${
                    dayIndex % 2 ? "bg-slate-50" : "bg-white"
                  } ${dayIndex > 0 ? "border-l-2 border-l-slate-300" : ""}`}
                >
                  {t(`timetable.weekday.${WEEKDAY_KEY[day]}`)}
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  /* `top-8` is exactly the `h-8` pinned on the day row above;
                     keep the two in step. It was a measured `top-[33px]`,
                     which misaligns the moment that row's type changes. */
                  className={`sticky top-8 z-20 h-6 px-1 text-center text-[10px] font-bold tabular-nums text-slate-500 border-b-2 border-slate-200 ${col.headBand} ${col.edge}`}
                >
                  {col.period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classes.map((row, rowIndex) => (
              <tr key={row.group.uuid} className="group/row">
                <th
                  scope="row"
                  className={`sticky left-0 z-10 bg-white px-3 py-1 text-[11px] font-black text-slate-700 whitespace-nowrap border-r-2 border-slate-200 shadow-[6px_0_10px_-8px_rgba(15,23,42,0.25)] group-hover/row:bg-indigo-50/70 ${
                    gradeBreaks.has(rowIndex) ? "border-t-2 border-t-slate-200" : ""
                  }`}
                >
                  {row.group.code || row.group.name}
                </th>
                {columns.map((col) => (
                  <SummaryCell
                    key={col.key}
                    column={col}
                    group={row.group}
                    lesson={row.slots.get(col.key)}
                    density={density}
                    topRule={gradeBreaks.has(rowIndex)}
                    onOpen={open}
                    onClose={close}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The clip has to read as "there is more", or a 1800px table inside a
          hard rounded edge just looks cropped. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-12 rounded-r-2xl bg-gradient-to-l from-white to-transparent"
      />

      {active && <LessonCard active={active} />}
    </div>
  );
}

// One summary cell. `memo`'d because `active` lives on SummaryGrid, so without
// a boundary here every hover re-rendered all ~1200 cells — each rebuilding a
// swatch, a joined teacher list, three template-literal class strings and a
// six-part aria-label. Its props are already stable per cell.
const SummaryCell = memo(function SummaryCell({
  column, group, lesson, density, topRule, onOpen, onClose,
}) {
  const top = topRule ? "border-t-2 border-t-slate-200" : "";
  const shell = `p-1 ${column.edge} ${column.band} ${top} group-hover/row:bg-indigo-50/40`;

  // A free period is drawn as NOTHING. Any positive styling of emptiness costs
  // ink in proportion to the number of empty cells, which on a whole-school
  // grid is most of them — the day band carries the lattice instead.
  if (!lesson) {
    return (
      <td className={shell}>
        <div className="min-w-[2.75rem] h-7" />
      </td>
    );
  }

  const subject = lesson.subject?.code || lesson.subject?.name || "";
  const teachers = lesson.teachers.map((x) => x.name).join(", ");
  // Keyed on the same string the draft board uses, so a committed board keeps
  // the colours it was reviewed in.
  const swatch = subjectColor(subject);
  const payload = { group, lesson, weekday: column.day, period: column.period };

  return (
    <td className={shell}>
      <button
        type="button"
        onMouseEnter={(e) => onOpen(e, payload)}
        onMouseLeave={onClose}
        onFocus={(e) => onOpen(e, payload, { immediate: true })}
        onBlur={onClose}
        aria-label={[
          group.code || group.name,
          column.dayName,
          column.periodName,
          lesson.subject?.name || subject,
          teachers,
          lesson.room?.code || lesson.room?.name,
        ]
          .filter(Boolean)
          .join(", ")}
        /* The colour lives in a 3px LEFT RAIL, not only in the fill. At 44px a
           3%-saturation wash is invisible from two feet; a saturated rail
           survives, and aSc uses exactly this for dense colour printing. */
        style={{
          backgroundColor: swatch.fill,
          borderColor: swatch.border,
          color: swatch.text,
          boxShadow: `inset 3px 0 0 0 ${swatch.accent}`,
        }}
        className="w-full min-w-[2.75rem] rounded-md border pl-2 pr-1 py-1 text-left leading-tight transition-[filter,box-shadow] hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
      >
        <span className="block text-[10px] font-black truncate">
          {subject || "\u2022"}
        </span>
        {density === "full" && teachers && (
          <span className="block text-[9px] font-semibold opacity-75 truncate">
            {shortName(lesson.teachers[0]?.name)}
            {lesson.teachers.length > 1 ? " +" : ""}
          </span>
        )}
      </button>
    </td>
  );
});

// Full detail for one lesson, anchored to the cell. `position: fixed` off the
// cell's viewport rect rather than absolute inside the table: the table is a
// horizontally-scrolling container, and an absolutely-positioned child of it
// would be clipped the moment the card sat near the right-hand edge.
function LessonCard({ active }) {
  const { t } = useTranslation();
  const { rect, lesson, group, weekday, period } = active;

  // Clamp into the viewport so a cell in the last column does not push the card
  // off-screen. 260px is the card's max width below.
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - 268);
  // Prefer below; flip above when the cell is near the bottom of the window.
  const below = rect.bottom + 8;
  const flip = below + 150 > window.innerHeight;
  const top = flip ? Math.max(8, rect.top - 158) : below;

  return (
    <div
      role="tooltip"
      aria-hidden="true"
      style={{ position: "fixed", left, top, maxWidth: 260 }}
      className="z-50 rounded-xl border border-slate-200 bg-white p-3 shadow-xl pointer-events-none motion-safe:animate-[tt-pop_120ms_ease-out]"
    >
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
        {group.code || group.name} ·{" "}
        {t(`timetable.weekday.${WEEKDAY_KEY[weekday]}`)} ·{" "}
        {t("timetable.grid.periodN", { n: period })}
      </p>
      <p className="mt-1 text-sm font-black text-slate-900">
        {lesson.subject?.name || lesson.subject?.code || "—"}
      </p>
      {lesson.teachers.length > 0 && (
        <p className="mt-0.5 text-xs font-semibold text-slate-600">
          {lesson.teachers.map((x) => x.name).join(", ")}
        </p>
      )}
      {lesson.room && (
        <p className="mt-0.5 text-[11px] font-medium text-slate-500">
          {lesson.room.code || lesson.room.name}
        </p>
      )}
    </div>
  );
}

// One lesson. Which line leads depends on the axis: on a class week you already
// know the class, so the subject leads; on a teacher or room week the class is
// the thing that identifies the lesson.
function Cell({ lesson, axis }) {
  const subject = lesson.subject?.code || lesson.subject?.name || "";
  const group = lesson.group?.code || lesson.group?.name || "";
  const lead = axis === "class" ? subject : group;
  const second = axis === "class" ? group : subject;
  const swatch = subjectColor(subject);

  return (
    // Same swatch and the same left accent rail as the draft board's card, so a
    // committed board still looks like the board it came from.
    <div
      style={{
        backgroundColor: swatch.fill,
        borderColor: swatch.border,
        color: swatch.text,
        boxShadow: `inset 3px 0 0 0 ${swatch.accent}`,
      }}
      className="rounded-lg border pl-2.5 pr-2 py-1.5"
    >
      <p className="text-[11px] font-black leading-tight truncate">{lead || "—"}</p>
      {second && (
        <p className="text-[10px] font-semibold opacity-75 leading-tight truncate">
          {second}
        </p>
      )}
      {lesson.teachers.length > 0 && axis !== "teacher" && (
        <p className="mt-0.5 text-[10px] font-medium opacity-70 leading-tight truncate">
          {lesson.teachers.map((x) => x.name).join(", ")}
        </p>
      )}
      {/* The room is a chip, not a fourth identical grey line — teachers and
          room were typographically identical and merged into one blob. Same
          treatment the board's card uses. */}
      {lesson.room && (
        <span className="mt-1 inline-block rounded bg-white/70 px-1 text-[10px] font-bold leading-tight">
          {lesson.room.code || lesson.room.name}
        </span>
      )}
    </div>
  );
}

// First-load placeholder, shaped like the table it becomes.
//
// It used to be two bars — a 40px pill over a 256px block — which is neither of
// the two tables this page renders, so the content jumped on arrival and the
// page's height changed twice. The two layouts are genuinely different shapes
// (the summary is a frozen first column with 40 narrow ones; a week is five wide
// day columns down the periods), and the skeleton has to be whichever one is
// about to appear or it is just a differently-shaped jump.
function GridSkeleton({ axis }) {
  if (axis === "school") {
    return (
      <div aria-busy="true" className={`${CARD} overflow-hidden p-3`}>
        <div className="flex gap-2">
          <Skeleton className="h-6 w-24 shrink-0" />
          {Array.from({ length: 5 }).map((_, day) => (
            <div key={day} className="flex flex-1 gap-1">
              {Array.from({ length: 6 }).map((__, period) => (
                <Skeleton key={period} className="h-6 flex-1" />
              ))}
            </div>
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, row) => (
          <div key={row} className="mt-1.5 flex gap-2">
            <Skeleton className="h-7 w-24 shrink-0" />
            {Array.from({ length: 5 }).map((__, day) => (
              <div key={day} className="flex flex-1 gap-1">
                {Array.from({ length: 6 }).map((___, period) => (
                  <Skeleton
                    key={period}
                    // Roughly the density of a real week — a solid block of
                    // cells would promise a fuller timetable than any school
                    // has, and free periods are most of the grid.
                    className={`h-7 flex-1 ${(row + day + period) % 3 === 0 ? "opacity-0" : ""}`}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div aria-busy="true" className={`${CARD} p-3`}>
      <div className="flex gap-2 px-2 py-2">
        <Skeleton className="h-3 w-12 shrink-0" />
        {Array.from({ length: 5 }).map((_, day) => (
          <Skeleton key={day} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: 7 }).map((_, period) => (
        <div key={period} className="flex items-start gap-2 px-2 py-1">
          <Skeleton className="h-10 w-12 shrink-0" />
          {Array.from({ length: 5 }).map((__, day) => (
            <Skeleton
              key={day}
              className={`h-12 flex-1 rounded-lg ${(period + day) % 4 === 0 ? "opacity-0" : ""}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyGrid() {
  const { t } = useTranslation();
  return (
    <EmptyState
      iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      title={t("timetable.grid.empty")}
      hint={t("timetable.grid.emptyHint")}
      action={
        <Link to="/timetable/generate" className={BTN.primary}>
          {t("timetable.tab.generate")}
        </Link>
      }
    />
  );
}
